import '../../../deal/activity-feed/tip-tap-editor/styles.css';

import { Button } from '@/components/ui/button';
import { CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Reply } from '@/models/registry';
import type { Trade, User } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { isEmpty, trim } from 'lodash';
import { PaperclipIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { getUserMentionIds } from '../../../deal/activity-feed/tip-tap-editor/get-user-mentions-ids';
import PaddockBubbleMenu from '../../../deal/activity-feed/tip-tap-editor/paddock-bubble-menu';
import PdkEditorContent from '../../../deal/activity-feed/tip-tap-editor/pdk-editor-content';
import { usePaddockEditor } from '../../../deal/activity-feed/tip-tap-editor/use-paddock-editor';

export const OPTIMISTIC_REPLY_ID_PREFIX = 'optimistic-reply-';

const EditTradeReplyTiptap = ({
  tradeId,
  reply,
  setIsEditingReply,
  className,
}: {
  tradeId: Trade['id'];
  reply: Reply;
  setIsEditingReply: (isEditing: boolean) => void;
  className?: string;
}) => {
  const initialContent = useMemo(() => {
    try {
      // DEV NOTE: comments and replies are stringified json
      // thus we need to parse it
      // return JSON.parse(reply.content);
      return reply.content;
    } catch (error) {
      console.error('Error parsing initialContent', error);
      return '';
    }
  }, [reply.content]);

  const { editor } = usePaddockEditor({
    initialContent,
  });

  const resetEditor = useCallback(() => {
    editor?.commands.setContent('');
  }, [editor]);

  const editReply = useMutation({
    mutationFn: async ({
      replyId,
      commentId,
      content,
      text,
      mentions,
    }: {
      replyId: string;
      commentId: string;
      content: string;
      text: string;
      mentions: User['id'][];
    }) => {
      const response = await pdkAxios
        .post(
          `/trades/${tradeId}/comments/${commentId}/reply/${replyId}/edit`,
          {
            content,
            text,
            mentions,
          },
        )
        .catch((e) => {
          console.error('Error editing trade reply', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      setIsEditingReply(false);
      resetEditor();
    },
    onError: (error, variables) => {
      // UX Note: restore the content of the editor
      editor?.commands.setContent(JSON.parse(variables?.content || '{}'));

      toast({
        title: 'Error editing reply',
        description: error.message,
      });
      console.error('Error editing trade reply', error, variables);
    },
  });

  const handleSubmitComment = () => {
    const content = editor?.getJSON();
    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    editReply.mutate({
      replyId: reply.id,
      commentId: reply.comment_id,
      content: JSON.stringify(content),
      text,
      mentions: mentionIds,
    });
  };

  const isExpanded = true;

  useEffect(() => {
    editor?.commands.focus('end');
  }, [editor]);

  const handleClose = useCallback(() => {
    setIsEditingReply(false);
  }, [setIsEditingReply]);

  return (
    <div className={className}>
      <div className="flex flex-col gap-4">
        <CardHeader className="p-0">
          <CardDescription>
            <PaddockBubbleMenu editor={editor} />

            <PdkEditorContent
              editor={editor}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' && e.metaKey) ||
                  (e.key === 'Enter' && e.ctrlKey)
                ) {
                  e.preventDefault();
                  handleSubmitComment();
                }

                if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                  handleClose();
                }
              }}
            />
          </CardDescription>
        </CardHeader>
        <CardFooter className="p-0">
          <motion.div
            className="flex flex-row w-full justify-between gap-2 h-10 items-center py-2"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <PaperclipIcon
              className={cn(
                'size-4 cursor-pointer text-gray-500 hover:fill-gray-100',
                {},
              )}
              onClick={() => {
                toast({
                  title: 'Feature not implemented yet',
                  description:
                    "Slack us if you want the 'comments attachments' feature",
                });
              }}
            />

            <div className="flex flex-row gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleClose}>
                    Cancel
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Cancel</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSubmitComment}
                  >
                    Save
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Submit Comment</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_SUBMIT_COMMENT}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
            </div>
          </motion.div>
        </CardFooter>
      </div>
    </div>
  );
};

export default EditTradeReplyTiptap;
